import type { Entry, Scenario } from "@/domain/entities";
import { pickText, type Locale } from "@/domain/localized";
import type { Period } from "@/domain/reports/periods";
import type { Report } from "@/domain/reports/ReportBuilder";
import { ScenarioRunner } from "@/domain/scenario/ScenarioRunner";
import { formatAnswer, statusLabel } from "../format";
import type { Strings } from "../i18n/strings";

export interface ExportInput {
  scenario: Scenario;
  entries: Entry[];
  period: Period;
  report: Report;
  locale: Locale;
  t: Strings;
  /** Lines already prepared for the Reports screen: question label + its summary lines. */
  summary: { label: string; lines: string[] }[];
}

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

/** One row per entry, one column per question; UTF-8 with a BOM so spreadsheets read Cyrillic correctly. */
export function buildCsv({ scenario, entries, period, locale, t }: ExportInput): string {
  const questions = scenario.questions;
  const header = [t.reports.dateColumn, t.reports.entryColumn, t.reports.statusColumn, ...questions.map((q) => pickText(q.text, locale))];
  const runner = new ScenarioRunner(scenario);
  const rows = entries
    .filter((e) => !e.deleted && e.date >= period.from && e.date <= period.to)
    .sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt));
  let dayCounter: { date: string; n: number } = { date: "", n: 0 };
  const lines = rows.map((entry) => {
    dayCounter = entry.date === dayCounter.date ? { date: entry.date, n: dayCounter.n + 1 } : { date: entry.date, n: 1 };
    const visible = new Set(runner.visibleQuestions(entry.answers).map((q) => q.id));
    const cells = [
      entry.date,
      String(dayCounter.n),
      statusLabel(entry.status, t),
      ...questions.map((q) => (visible.has(q.id) && entry.status !== "skipped" ? formatAnswer(q, entry.answers[q.id], locale, t) : "")),
    ];
    return cells.map(csvCell).join(",");
  });
  return "﻿" + [header.map(csvCell).join(","), ...lines].join("\r\n") + "\r\n";
}

/** The platform share sheet when it accepts files, otherwise a plain download. */
export async function shareFile(file: File, title: string): Promise<"shared" | "downloaded" | "cancelled"> {
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean; share?: (data: ShareData) => Promise<void> };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title });
      return "shared";
    } catch (cause) {
      if (cause instanceof Error && cause.name === "AbortError") return "cancelled";
      throw cause;
    }
  }
  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return "downloaded";
}

const fontCache = new Map<string, Promise<ArrayBuffer>>();

/** PT Sans covers Latin and Cyrillic; Devanagari needs Noto Sans Devanagari (both OFL, shipped with the app). */
export function pdfFontUrl(locale: Locale): string {
  return locale === "hi" ? "/fonts/NotoSansDevanagari-Regular.ttf" : "/fonts/PTSans-Regular.ttf";
}

/** Fetched once per font. */
export function loadPdfFont(locale: Locale): Promise<ArrayBuffer> {
  const url = pdfFontUrl(locale);
  let cached = fontCache.get(url);
  if (!cached) {
    cached = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`Font download failed: ${r.status}`);
      return r.arrayBuffer();
    });
    fontCache.set(url, cached);
  }
  return cached;
}
